import { X } from 'lucide-react';
import { WizardProgress, type WizardStep } from './WizardProgress';
import { WizardNavigation } from './WizardNavigation';

interface WizardProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  steps: WizardStep[];
  currentStep: number;
  stepValidity?: boolean[];
  onStepClick?: (step: number) => void;
  onPrev: () => void;
  onNext: () => void;
  onSubmit: () => void;
  onClose: () => void;
  canGoNext: boolean;
  isSubmitting?: boolean;
  submitLabel?: string;
  allowStepNavigation?: boolean;
}

export function Wizard({
  children,
  title,
  subtitle,
  steps,
  currentStep,
  stepValidity = [],
  onStepClick,
  onPrev,
  onNext,
  onSubmit,
  onClose,
  canGoNext,
  isSubmitting = false,
  submitLabel,
  allowStepNavigation = true,
}: WizardProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Wizard Container */}
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="px-8 pt-6 pb-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                {title}
              </h2>
              {subtitle && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {subtitle}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300
                       hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress */}
          <WizardProgress
            steps={steps}
            currentStep={currentStep}
            onStepClick={onStepClick}
            stepValidity={stepValidity}
            allowNavigation={allowStepNavigation}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {children}
        </div>

        {/* Footer Navigation */}
        <div className="px-8 pb-6">
          <WizardNavigation
            currentStep={currentStep}
            totalSteps={steps.length}
            onPrev={onPrev}
            onNext={onNext}
            onSubmit={onSubmit}
            onCancel={onClose}
            canGoNext={canGoNext}
            isSubmitting={isSubmitting}
            submitLabel={submitLabel}
          />
        </div>
      </div>
    </div>
  );
}
